import { apiRequest } from './apiClient';
import { AUTH_ROLES } from '../utils/authConstants';
import { getApiUrl } from '../config/api';
import AuthServiceV2 from './authServiceV2';

class QuizService {
  static unwrapData(resultData) {
    if (resultData && typeof resultData === 'object' && 'data' in resultData) return resultData.data;
    return resultData;
  }

  static getMediaUrl(url) {
    if (!url) return '';
    if (/^https?:\/\//i.test(url)) return url;
    return getApiUrl(url);
  }

  static async multipartRequest(endpoint, formData) {
    const token = AuthServiceV2.getToken(AUTH_ROLES.ADMIN) || localStorage.getItem('access_token');

    const response = await fetch(getApiUrl(endpoint), {
      method: 'POST',
      headers: {
        accept: 'application/json',
        ...(token && { Authorization: `Bearer ${token}` })
      },
      body: formData
    });

    const text = await response.text();
    let result = null;
    if (text) {
      try {
        result = JSON.parse(text);
      } catch {
        result = { message: text };
      }
    }

    const backendStatus = result?.status;
    const backendCode = result?.status_code;
    const isBackendSuccess = backendStatus === true && (backendCode === 200 || backendCode === 201);

    if (response.ok && (isBackendSuccess || !('status' in (result || {})))) {
      return {
        success: true,
        data: this.unwrapData(result?.data ?? result),
        message: result?.message || 'Upload successful',
        raw: result
      };
    }

    return {
      success: false,
      message: result?.message || `Upload failed (${response.status})`,
      raw: result
    };
  }

  // Helper function to check if user is authenticated
  static isAuthenticated() {
    return !!localStorage.getItem('access_token');
  }

  // Create a new quiz
  static async createQuiz(quizData) {
    try {
      const result = await apiRequest({
        endpoint: '/admin/api/admin/quiz',
        method: 'POST',
        auth: true,
        role: AUTH_ROLES.ADMIN,
        body: quizData,
        showSuccess: false,  // Disable API-level toast to prevent double toast
        successMessage: 'Quiz created successfully'
      });

      if (result.success) {
        return {
          success: true,
          data: result.data
        };
      } else {
        return {
          success: false,
          message: result.message || 'Failed to create quiz'
        };
      }
    } catch (error) {
      console.error('Create quiz error:', error);
      return {
        success: false,
        message: 'Network error. Please check your connection.'
      };
    }
  }

  // Delete question
  static async deleteQuestion(questionId, quizId) {
    try {
      if (!quizId) {
        return { success: false, message: 'Quiz ID is required to delete a question' };
      }

      const result = await apiRequest({
        endpoint: `/admin/api/quiz/${quizId}/questions/${questionId}`,
        method: 'DELETE',
        auth: true,
        role: AUTH_ROLES.ADMIN,
        showSuccess: false,  // Disable API-level toast to prevent double toast
        successMessage: 'Question deleted successfully'
      });

      if (result.success) {
        return { success: true, message: result.message || 'Question deleted successfully' };
      }

      return {
        success: false,
        message: result.message || 'Failed to delete question'
      };
    } catch (error) {
      console.error('Delete question error:', error);
      return {
        success: false,
        message: 'Network error. Please check your connection.'
      };
    }
  }

  // Update quiz
  static async updateQuiz(quizId, quizData) {
    try {
      const result = await apiRequest({
        endpoint: `/admin/api/admin/quiz/${quizId}`,
        method: 'PATCH',
        auth: true,
        role: AUTH_ROLES.ADMIN,
        body: quizData,
        showSuccess: false,  // Disable API-level toast to prevent double toast
        successMessage: 'Quiz updated successfully'
      });

      if (result.success) {
        return {
          success: true,
          data: result.data
        };
      } else {
        return {
          success: false,
          message: result.message || 'Failed to update quiz'
        };
      }
    } catch (error) {
      console.error('Update quiz error:', error);
      return {
        success: false,
        message: 'Network error. Please check your connection.'
      };
    }
  }

  // Get events for dropdown
  static async getEvents() {
    try {
      const result = await apiRequest({
        endpoint: '/admin/api/events',
        method: 'GET',
        auth: true,
        role: AUTH_ROLES.ADMIN,
        showSuccess: false
      });

      if (result.success) {
        return {
          success: true,
          data: this.unwrapData(result.data)
        };
      } else {
        return {
          success: false,
          message: result.message || 'Failed to fetch events'
        };
      }
    } catch (error) {
      console.error('Get events error:', error);
      return {
        success: false,
        message: 'Network error. Please check your connection.'
      };
    }
  }

  // Add question to quiz
  static async createQuestion(quizId, questionData) {
    try {
      console.log('🔍 QuizService.createQuestion - questionData.designationId:', questionData.designationId);
      console.log('🔍 QuizService.createQuestion - typeof questionData.designationId:', typeof questionData.designationId);
      
      const requestBody = {
        question_text: questionData.question_text,
        correct_answer: questionData.correct_answer,
        question_type: questionData.question_type || 'radio',
        points: questionData.points || 0,
        time_limit: questionData.time_limit,
        designationId: questionData.designationId
      };
      
      console.log('🔍 QuizService.createQuestion - requestBody.designationId:', requestBody.designationId);
      console.log('🔍 QuizService.createQuestion - requestBody:', JSON.stringify(requestBody, null, 2));

      if (Array.isArray(questionData.options) && questionData.options.length > 0) {
        requestBody.options = questionData.options;
      }

      console.log('🔍 QuizService.createQuestion - final requestBody before apiRequest:', JSON.stringify(requestBody, null, 2));

      const result = await apiRequest({
        endpoint: `/admin/api/quiz/${quizId}/questions`,
        method: 'POST',
        auth: true,
        role: AUTH_ROLES.ADMIN,
        body: requestBody,
        showSuccess: false,  // Disable API-level toast to prevent double toast
        successMessage: 'Question added successfully'
      });

      if (result.success) {
        return {
          success: true,
          data: this.unwrapData(result.data)
        };
      } else {
        return {
          success: false,
          message: result.message || 'Failed to add question'
        };
      }
    } catch (error) {
      console.error('Add question error:', error);
      return {
        success: false,
        message: 'Network error. Please check your connection.'
      };
    }
  }

  // Backward compatible name
  static async addQuestion(quizId, questionData) {
    return this.createQuestion(quizId, questionData);
  }

  static async createQuestionOption(quizId, questionId, optionText) {
    try {
      const result = await apiRequest({
        endpoint: `/admin/api/quiz/${quizId}/questions/${questionId}/options`,
        method: 'POST',
        auth: true,
        role: AUTH_ROLES.ADMIN,
        body: { option_text: optionText },
        showSuccess: false
      });

      if (result.success) {
        return {
          success: true,
          data: this.unwrapData(result.data)
        };
      }

      return {
        success: false,
        message: result.message || 'Failed to create option'
      };
    } catch (error) {
      console.error('Create question option error:', error);
      return {
        success: false,
        message: 'Network error. Please check your connection.'
      };
    }
  }

  static async uploadQuestionOptionImage(quizId, questionId, file, optionText = '') {
    try {
      const formData = new FormData();
      formData.append('image', file);
      if (optionText && optionText.trim()) {
        formData.append('option_text', optionText.trim());
      }

      return await this.multipartRequest(
        `/admin/api/quiz/${quizId}/questions/${questionId}/options/image`,
        formData
      );
    } catch (error) {
      console.error('Upload option image error:', error);
      return {
        success: false,
        message: 'Network error. Please check your connection.'
      };
    }
  }

  static async uploadQuestionImage(quizId, questionId, file) {
    try {
      const formData = new FormData();
      formData.append('image', file);

      return await this.multipartRequest(
        `/admin/api/quiz/${quizId}/questions/${questionId}/image`,
        formData
      );
    } catch (error) {
      console.error('Upload question image error:', error);
      return {
        success: false,
        message: 'Network error. Please check your connection.'
      };
    }
  }

  // List questions for a quiz
  static async getQuestions(quizId) {
    try {
      const result = await apiRequest({
        endpoint: `/admin/api/quiz/${quizId}/questions`,
        method: 'GET',
        auth: true,
        role: AUTH_ROLES.ADMIN,
        showSuccess: false
      });

      if (result.success) {
        return {
          success: true,
          data: this.unwrapData(result.data)
        };
      }

      return {
        success: false,
        message: result.message || 'Failed to fetch questions'
      };
    } catch (error) {
      console.error('Get questions error:', error);
      return {
        success: false,
        message: 'Network error. Please check your connection.'
      };
    }
  }

  // Get single question
  static async getQuestion(quizId, questionId) {
    try {
      const result = await apiRequest({
        endpoint: `/admin/api/quiz/${quizId}/questions/${questionId}`,
        method: 'GET',
        auth: true,
        role: AUTH_ROLES.ADMIN,
        showSuccess: false
      });

      if (result.success) {
        return {
          success: true,
          data: this.unwrapData(result.data)
        };
      }

      return {
        success: false,
        message: result.message || 'Failed to fetch question'
      };
    } catch (error) {
      console.error('Get question error:', error);
      return {
        success: false,
        message: 'Network error. Please check your connection.'
      };
    }
  }

  // Update question
  static async updateQuestion(quizId, questionId, questionData) {
    try {
      const requestBody = {};

      [
        'question_text',
        'correct_answer',
        'question_type',
        'points',
        'time_limit',
        'designationId',
        'question_image_url'
      ].forEach((key) => {
        if (questionData[key] !== undefined) {
          requestBody[key] = questionData[key];
        }
      });

      if (Array.isArray(questionData.options)) {
        requestBody.options = questionData.options;
      }

      const result = await apiRequest({
        endpoint: `/admin/api/quiz/${quizId}/questions/${questionId}`,
        method: 'PATCH',
        auth: true,
        role: AUTH_ROLES.ADMIN,
        body: requestBody,
        showSuccess: false,  // Disable API-level toast to prevent double toast
        successMessage: 'Question updated successfully'
      });

      if (result.success) {
        return {
          success: true,
          data: this.unwrapData(result.data)
        };
      }

      return {
        success: false,
        message: result.message || 'Failed to update question'
      };
    } catch (error) {
      console.error('Update question error:', error);
      return {
        success: false,
        message: 'Network error. Please check your connection.'
      };
    }
  }

  // Get all quizzes with questions
  static async getQuizzesWithQuestions(page = 1, limit = 20) {
    try {
      let url = '/admin/api/admin/quiz/with-questions';
      
      // Add query parameters for pagination
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const result = await apiRequest({
        endpoint: url,
        method: 'GET',
        auth: true,
        role: AUTH_ROLES.ADMIN,
        showSuccess: false
      });

      if (result.success) {
        return {
          success: true,
          data: this.unwrapData(result.data)
        };
      } else {
        return {
          success: false,
          message: result.message || 'Failed to fetch quizzes with questions'
        };
      }
    } catch (error) {
      console.error('Get quizzes with questions error:', error);
      return {
        success: false,
        message: 'Network error. Please check your connection.'
      };
    }
  }

  // Update quiz
  static async updateQuiz(quizId, quizData) {
    try {
      const result = await apiRequest({
        endpoint: `/admin/api/admin/quiz/${quizId}`,
        method: 'PATCH',
        auth: true,
        role: AUTH_ROLES.ADMIN,
        body: quizData,
        showSuccess: false,  // Disable API-level toast to prevent double toast
        successMessage: 'Quiz updated successfully'
      });

      if (result.success) {
        return {
          success: true,
          data: result.data
        };
      } else {
        return {
          success: false,
          message: result.message || 'Failed to update quiz'
        };
      }
    } catch (error) {
      console.error('Update quiz error:', error);
      return {
        success: false,
        message: 'Network error. Please check your connection.'
      };
    }
  }

  // Get all quizzes
  static async getQuizzes(page = null, limit = null) {
    try {
      let url = '/admin/api/admin/quiz';
      
      // Add query parameters if pagination is needed
      const params = new URLSearchParams();
      if (page !== null) params.append('page', page.toString());
      if (limit !== null) params.append('limit', limit.toString());
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const result = await apiRequest({
        endpoint: url,
        method: 'GET',
        auth: true,
        role: AUTH_ROLES.ADMIN,
        showSuccess: false
      });

      if (result.success) {
        return {
          success: true,
          data: this.unwrapData(result.data)
        };
      } else {
        return {
          success: false,
          message: result.message || 'Failed to fetch quizzes'
        };
      }
    } catch (error) {
      console.error('Get quizzes error:', error);
      return {
        success: false,
        message: 'Network error. Please check your connection.'
      };
    }
  }

  // Backward compatible name
  static async getQuizQuestions(quizId) {
    return this.getQuestions(quizId);
  }

  // Delete quiz 
  static async deleteQuiz(quizId) {
    try {
      console.log(`Starting deletion of quiz ${quizId}`);

      console.log('Deleting the quiz');
      // Now delete quiz
      const result = await apiRequest({
        endpoint: `/admin/api/admin/quiz/${quizId}`,
        method: 'DELETE',
        auth: true,
        role: AUTH_ROLES.ADMIN,
        showSuccess: false,  // Disable API-level toast to prevent double toast
        successMessage: 'Quiz deleted successfully'
      });

      console.log('Quiz deletion result:', result);
      
      if (result.success) {
        return { success: true, message: result.message || 'Quiz deleted successfully' };
      } else {
        return {
          success: false,
          message: result.message || 'Failed to delete quiz'
        };
      }
    } catch (error) {
      console.error('Delete quiz error:', error);
      return {
        success: false,
        message: 'Network error. Please check your connection.'
      };
    }
  }

  // Get active quiz for user
  static async getUserQuiz() {
    try {
      const result = await apiRequest({
        endpoint: '/admin/api/user/quiz',
        method: 'GET',
        auth: true,
        role: AUTH_ROLES.USER,
        showSuccess: false
      });

      if (result.success) {
        return {
          success: true,
          data: result.data
        };
      } else {
        return {
          success: false,
          message: result.message || 'Failed to fetch quiz'
        };
      }
    } catch (error) {
      console.error('Get user quiz error:', error);
      return {
        success: false,
        message: 'Network error. Please check your connection.'
      };
    }
  }

  // Start or resume active quiz session
  static async startQuizSession() {
    try {
      const result = await apiRequest({
        endpoint: '/admin/api/user/submit-quiz/session/start',
        method: 'POST',
        auth: true,
        role: AUTH_ROLES.USER,
        showSuccess: false
      });

      if (result.success) {
        return { success: true, data: result.data };
      }

      return {
        success: false,
        message: result.message || 'Failed to start quiz session'
      };
    } catch (error) {
      console.error('Start quiz session error:', error);
      return {
        success: false,
        message: 'Network error. Please check your connection.'
      };
    }
  }

  // Get current quiz session
  static async getCurrentQuizSession() {
    try {
      const result = await apiRequest({
        endpoint: '/admin/api/user/submit-quiz/session/current',
        method: 'GET',
        auth: true,
        role: AUTH_ROLES.USER,
        showSuccess: false,
        suppressErrorToast: true
      });

      if (result.success) {
        return { success: true, data: result.data };
      }

      return {
        success: false,
        message: result.message || 'Failed to load quiz session'
      };
    } catch (error) {
      console.error('Get quiz session error:', error);
      return {
        success: false,
        message: 'Network error. Please check your connection.'
      };
    }
  }

  // Autosave answers for active quiz session
  static async saveSessionAnswers(sessionId, answers, currentQuestionIndex = 0) {
    try {
      const result = await apiRequest({
        endpoint: `/admin/api/user/submit-quiz/session/${sessionId}/answers`,
        method: 'POST',
        auth: true,
        role: AUTH_ROLES.USER,
        body: {
          current_question_index: currentQuestionIndex,
          answers
        },
        showSuccess: false,
        suppressErrorToast: true
      });

      if (result.success) {
        return { success: true, data: result.data };
      }

      return {
        success: false,
        message: result.message || 'Failed to save quiz answers'
      };
    } catch (error) {
      console.error('Save quiz answers error:', error);
      return {
        success: false,
        message: 'Network error. Please check your connection.'
      };
    }
  }

  // Get supported compiler languages
  static async getCompilerLanguages() {
    try {
      const result = await apiRequest({
        endpoint: '/admin/api/user/compiler/languages',
        method: 'GET',
        auth: true,
        role: AUTH_ROLES.USER,
        showSuccess: false
      });

      if (result.success) {
        return {
          success: true,
          data: result.data
        };
      } else {
        return {
          success: false,
          message: result.message || 'Failed to load supported languages'
        };
      }
    } catch (error) {
      console.error('Get compiler languages error:', error);
      return {
        success: false,
        message: 'Network error. Please check your connection.'
      };
    }
  }

  // Execute code
  static async executeCode(code, language, stdinInput = '') {
    try {
      const result = await apiRequest({
        endpoint: '/admin/api/user/compiler/execute',
        method: 'POST',
        auth: true,
        role: AUTH_ROLES.USER,
        body: { code, language, stdinInput: stdinInput },
        showSuccess: false
      });

      if (result.success) {
        return {
          success: true,
          data: result.data
        };
      } else {
        return {
          success: false,
          message: result.message || 'Code execution failed'
        };
      }
    } catch (error) {
      console.error('Execute code error:', error);
      return {
        success: false,
        message: 'Network error. Please check your connection.'
      };
    }
  }

  // Bulk submit quiz answers
  static async submitBulkAnswers(answers, violationReason = "", sessionId = null) {
    try {
      const result = await apiRequest({
        endpoint: '/admin/api/user/submit-quiz/submit',
        method: 'POST',
        auth: true,
        role: AUTH_ROLES.USER,
        body: { 
          ...(sessionId ? { sessionId } : {}),
          violation_reason: violationReason,
          answers 
        },
        showSuccess: false,  // Disable API-level toast to prevent double toast
        successMessage: 'Answers submitted successfully'
      });

      if (result.success) {
        return {
          success: true,
          data: result.data
        };
      } else {
        return {
          success: false,
          message: result.message || 'Quiz submission failed'
        };
      }
    } catch (error) {
      console.error('Bulk submit error:', error);
      return {
        success: false,
        message: 'Network error. Please check your connection.'
      };
    }
  }
}

export default QuizService;
